import React, { useEffect, useState } from "react";
import { Button } from "web/react/emo/button";
import { useClickTracker } from "web/react/hooks/use-click-tracker/use-click-tracker";
import analytics from "web/script/analytics/analytics";
import { AdjustLinkData, getAdjustCampaignName, getLinkData } from "web/script/utils/adjust";
import { NotQuiteAURL } from "web/script/utils/url";

interface AdjustLinkProps {
    adjustLink: string;
    title: string;
    className?: string;
    children?: JSX.Element;
    eventLabel?: string;
    primaryButton?: boolean;
    appDeepLinkURL?: URL | NotQuiteAURL;
    showAppIcon?: boolean;
    financialIncentiveWebviewToken?: string;
    beforeOnClick?: React.EventHandler<React.MouseEvent>;
}

function AdjustLink({
    title,
    className,
    eventLabel,
    adjustLink,
    primaryButton,
    appDeepLinkURL,
    showAppIcon = false,
    financialIncentiveWebviewToken,
    beforeOnClick,
    children,
}: AdjustLinkProps): JSX.Element {
    let trackedClick = useClickTracker("web_to_app", "click", eventLabel, true, {
        campaign_name: getAdjustCampaignName(adjustLink),
    });

    function onClick(event): void {
        beforeOnClick?.(event);
        trackedClick(event);
    }

    const [adjustHref, setAdjustHref] = useState<string>(adjustLink);

    useEffect(() => {
        function updateHref(): void {
            const adjustReferralLink = new URL(adjustLink);
            const adjustLinkData: AdjustLinkData = getLinkData(
                appDeepLinkURL,
                financialIncentiveWebviewToken
            );
            for (const adjustDataProperty in adjustLinkData) {
                adjustReferralLink.searchParams.set(
                    adjustDataProperty,
                    adjustLinkData[adjustDataProperty]
                );
            }

            setAdjustHref(adjustReferralLink.toString());
        }

        if (!adjustLink) return;

        updateHref();
        analytics.on("pageViewIdChanged", updateHref);
        return function cleanup(): void {
            analytics.off("pageViewIdChanged", updateHref);
        };
    }, [adjustLink, appDeepLinkURL, financialIncentiveWebviewToken]);

    if (primaryButton) {
        return (
            <Button
                onClick={onClick}
                title={title}
                width="full"
                href={adjustHref}
                icon={showAppIcon ? "lyst-app-icon" : undefined}
            />
        );
    }

    return (
        <a
            onClick={onClick}
            className={className}
            href={adjustHref}
            title={title}
            aria-label={title}
        >
            {children}
        </a>
    );
}

export default AdjustLink;
